import { useCallback, useEffect, useState } from 'react';
import { useNotify, useRecordContext } from 'react-admin';
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Switch,
  Typography,
} from '@mui/material';
import { supabase } from '../lib/supabaseClient';
import { MARKETS, MarketCode, StaffMarketsRow } from '../inbox/staffMarkets';

/**
 * Markets on a staff profile (T-048): which markets' unclaimed leads this
 * person sees and is alerted for. Saved on each click — its own row in
 * `staff_markets`, apart from the profile form. Admins only (RLS).
 */
export const StaffMarketsCard = () => {
  const record = useRecordContext<{ id: string; name?: string }>();
  const notify = useNotify();
  const [row, setRow] = useState<StaffMarketsRow | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const profileId = record?.id ? String(record.id) : null;

  useEffect(() => {
    if (!profileId) return;
    let cancelled = false;
    void supabase
      .from('staff_markets')
      .select('profile_id, markets, sees_all')
      .eq('profile_id', profileId)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled) return;
        setRow((data as StaffMarketsRow | null) ?? null);
        setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [profileId]);

  const save = useCallback(
    async (next: { markets: MarketCode[]; sees_all: boolean }) => {
      if (!profileId) return;
      const previous = row;
      setRow({ profile_id: profileId, ...next });
      setSaving(true);
      const { data: auth } = await supabase.auth.getUser();
      const { error } = await supabase.from('staff_markets').upsert({
        profile_id: profileId,
        ...next,
        updated_at: new Date().toISOString(),
        updated_by: auth?.user?.id ?? null,
      });
      setSaving(false);
      if (error) {
        setRow(previous);
        notify(`Markets not saved: ${error.message}`, { type: 'error' });
      } else {
        notify('Markets saved', { type: 'info' });
      }
    },
    [profileId, row, notify]
  );

  if (!profileId || !loaded) return null;

  const markets = row?.markets ?? [];
  const seesAll = row?.sees_all ?? false;
  const toggleMarket = (code: MarketCode) => {
    const next = markets.includes(code)
      ? markets.filter((m) => m !== code)
      : [...markets, code];
    void save({
      markets: MARKETS.map((m) => m.code).filter((c) => next.includes(c)),
      sees_all: seesAll,
    });
  };

  return (
    <Card sx={{ mt: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Markets
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Which markets&apos; unclaimed leads {record?.name ?? 'this person'}{' '}
          sees in the inbox and is alerted for. Conversations they own always
          reach them, and anyone can still be assigned any lead.
          {!row && ' Not set up yet, so they see every market.'}
        </Typography>
        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                checked={seesAll}
                disabled={saving}
                onChange={(e) =>
                  void save({ markets, sees_all: e.target.checked })
                }
              />
            }
            label="Sees all markets"
          />
          <Box sx={{ pl: 1, opacity: seesAll ? 0.5 : 1 }}>
            {MARKETS.map((m) => (
              <FormControlLabel
                key={m.code}
                control={
                  <Checkbox
                    checked={markets.includes(m.code)}
                    disabled={saving || seesAll}
                    onChange={() => toggleMarket(m.code)}
                  />
                }
                label={m.label}
              />
            ))}
          </Box>
        </FormGroup>
        {row && !seesAll && markets.length === 0 && (
          <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
            No markets: not taking leads — no unclaimed leads or alerts, not even for unknown
            markets.
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

/** Every staff member's markets, for the Team list. */
export function useAllStaffMarkets(): Map<string, StaffMarketsRow> {
  const [rows, setRows] = useState<Map<string, StaffMarketsRow>>(new Map());
  useEffect(() => {
    let cancelled = false;
    void supabase
      .from('staff_markets')
      .select('profile_id, markets, sees_all')
      .then(({ data }) => {
        if (cancelled) return;
        setRows(
          new Map(
            ((data ?? []) as StaffMarketsRow[]).map((r) => [r.profile_id, r])
          )
        );
      });
    return () => {
      cancelled = true;
    };
  }, []);
  return rows;
}

export function staffMarketsSummary(row: StaffMarketsRow | undefined): string {
  if (!row) return 'All (not set up)';
  if (row.sees_all) return 'All';
  if (!row.markets.length) return 'None (not taking leads)';
  return MARKETS.filter((m) => row.markets.includes(m.code))
    .map((m) => m.short)
    .join(', ');
}
